import { useMutation, useQuery } from '@tanstack/react-query'
import queryClient from '@config/QueryClient'
import { StageInstanceVoteRequest, StageTransitionRequest } from '@portal/pkg-api/gen/stages'
import { StagesApi } from '@portal/pkg-api/admin'
import { EntityApi } from '@portal/pkg-api/navigator'
import { data } from '@portal/pkg-api/shared'
import { LocalDate, LocalDateTime, ZonedDateTime, ZoneId } from '@js-joda/core'
import { StageInstanceHistoryItem, StageInstanceSummary } from '@navigator/model/Stages'

const prefix = 'navigator.stages'

export const StageQueries = {
  entityCheckLock: (entityId: number) => ({
    queryKey: [`${prefix}.entityCheckLock`, entityId],
    queryFn: () => data(EntityApi.checkLockEntity(entityId)),
    staleTime: 0,
  }),
  entityCheckUnlock: (entityId: number) => ({
    queryKey: [`${prefix}.entityCheckUnlock`, entityId],
    queryFn: () => data(EntityApi.checkUnlockEntity(entityId)),
    staleTime: 0,
  }),
  entityStageHistory: (entityId: number) => ({
    queryKey: [`${prefix}.entityStageHistory`, entityId],
    queryFn: entityStageHistoryQueryFn(entityId),
    staleTime: 0,
  }),
  stageInstanceSummary: (entityId?: number) => ({
    queryKey: [`${prefix}.stageInstanceSummary`, entityId],
    queryFn: stageInstanceSummaryQueryFn(entityId!),
    enabled: !!entityId,
  }),
}

const entityStageHistoryQueryFn =
  (entityId: number) => async (): Promise<ReadonlyArray<StageInstanceHistoryItem>> => {
    const response = await data(StagesApi.getStageInstanceHistory(entityId))
    return response.map((item) => ({
      ...item,
      sourceTransition: item.sourceTransition
        ? {
            ...item.sourceTransition,
            createdBy: {
              id: item.sourceTransition.createdBy.id,
              email: item.sourceTransition.createdBy.email!,
              givenName: item.sourceTransition.createdBy.givenName!,
              familyName: item.sourceTransition.createdBy.familyName!,
            },
            createdDate: ZonedDateTime.parse(item.sourceTransition.createdDate),
          }
        : undefined,
      targetTransition: item.targetTransition
        ? {
            ...item.targetTransition,
            createdBy: {
              id: item.targetTransition.createdBy.id,
              email: item.targetTransition.createdBy.email!,
              givenName: item.targetTransition.createdBy.givenName!,
              familyName: item.targetTransition.createdBy.familyName!,
            },
            createdDate: ZonedDateTime.parse(item.targetTransition.createdDate),
          }
        : undefined,
      votes: item.votes.map((v) => ({
        ...v,
        voter: {
          id: v.voter.id,
          email: v.voter.email!,
          givenName: v.voter.givenName!,
          familyName: v.voter.familyName!,
        },
        createdDate: ZonedDateTime.of(LocalDateTime.parse(v.createdDate), ZoneId.UTC),
      })),
      createdDate: ZonedDateTime.parse(item.createdDate),
    }))
  }

const stageInstanceSummaryQueryFn =
  (entityId: number) => async (): Promise<StageInstanceSummary> => {
    const response = await data(StagesApi.getStageInstanceSummary(entityId))
    return {
      ...response,
      dueDate: response.dueDate ? LocalDate.parse(response.dueDate) : undefined,
      assignee: response.assignee
        ? {
            id: response.assignee.id,
            familyName: response.assignee.familyName!,
            givenName: response.assignee.givenName!,
            email: response.assignee.email!,
          }
        : undefined,
    }
  }

export function useEntityCheckLock(entityId: number) {
  return useQuery(StageQueries.entityCheckLock(entityId))
}

export function useEntityCheckUnlock(entityId: number) {
  return useQuery(StageQueries.entityCheckUnlock(entityId))
}

export function useEntityStageHistory(entityId: number) {
  return useQuery(StageQueries.entityStageHistory(entityId))
}

/**
 * @param options.enabled defer the request; the grid's row menu only needs the summary once it has
 *                        been opened, so it does not fetch one per visible row
 */
export function useStageInstanceSummary(entityId: number, options?: { enabled?: boolean }) {
  return useQuery({ ...StageQueries.stageInstanceSummary(entityId), ...options })
}

/** Drop the cached stage summary so the next stage menu reads the current required fields. */
export function invalidateStageInstanceSummary(entityId: number) {
  void queryClient.invalidateQueries({
    queryKey: StageQueries.stageInstanceSummary(entityId).queryKey,
  })
}

export function useSaveStageInstanceVote() {
  return useMutation<void, unknown, StageInstanceVotePayload>({
    mutationFn: ({ stageInstanceVoteRequest }) =>
      data(StagesApi.vote(stageInstanceVoteRequest.stageInstanceId, stageInstanceVoteRequest)),
    onSuccess: (_data, { entityId }) => {
      invalidateStageInstanceSummary(entityId)
    },
  })
}

export function useSaveStageInstanceTransition() {
  return useMutation<void, unknown, StageTransitionRequest>({
    mutationFn: (request) => data(StagesApi.transitionStage(request)),
    onSuccess: (_data, { entityId }) => {
      invalidateStageInstanceSummary(entityId)
    },
  })
}

export type StageInstanceVotePayload = {
  entityId: number
  stageInstanceVoteRequest: StageInstanceVoteRequest
}
